/**
 * Formato y lectura de numeros en castellano, para toda la app.
 *
 * - Al MOSTRAR: coma para los decimales y punto solo como separador visual de
 *   miles ("1.234.567,89"). El punto de miles se pone siempre, tambien en los
 *   numeros de cuatro cifras (Intl en es-ES los deja sin separar: "1234"),
 *   para que 1.234 y 1234 no convivan en pantalla.
 * - Al LEER lo que escribe una persona: la coma y el punto valen como
 *   separador decimal (ver parseLocaleNumber).
 *
 * Es independiente del idioma de la interfaz: un numero se ve igual en es y en.
 */

export const NUMBER_LOCALE = "es-ES";

type FormatOptions = Intl.NumberFormatOptions;

/** Intl.NumberFormat en castellano con separador de miles siempre activo. */
export const createNumberFormat = (options: FormatOptions = {}) =>
  new Intl.NumberFormat(NUMBER_LOCALE, {
    useGrouping: "always",
    ...options,
  } as FormatOptions);

const isNumber = (n: unknown): n is number =>
  typeof n === "number" && Number.isFinite(n);

/** Valor con numero de decimales exacto: 1234.5 → "1.234,50". */
export const formatFixed = (n: number, decimals = 2): string =>
  createNumberFormat({
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);

/** Entero con separador de miles: 1234567.4 → "1.234.567". */
export const formatInteger = (n: number): string =>
  createNumberFormat({ maximumFractionDigits: 0 }).format(n);

/**
 * Numero sin decimales de relleno: 1234.5 → "1.234,5", 3 → "3".
 * `maxDecimals` limita los decimales que se muestran.
 */
export const formatFlexible = (n: number, maxDecimals = 2): string =>
  createNumberFormat({
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  }).format(n);

/** Valor ya expresado en % (12.5 → "12,50 %"). */
export const formatPercentValue = (n: number, decimals = 2): string =>
  `${formatFixed(n, decimals)}\u00a0%`;

/** Importe en una moneda: (1234.5, "EUR") → "1.234,50 €". */
export const formatMoney = (
  n: number,
  currency = "EUR",
  options: { minDecimals?: number; maxDecimals?: number } = {}
): string => {
  const { minDecimals = 2, maxDecimals = minDecimals } = options;
  const config = {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: Math.max(minDecimals, maxDecimals),
  };
  try {
    return createNumberFormat({
      style: "currency",
      currency,
      ...config,
    }).format(n);
  } catch {
    // Codigo de moneda desconocido: numero + codigo, sin romper la pantalla.
    return `${createNumberFormat(config).format(n)} ${currency}`;
  }
};

/** Como formatFixed pero tolerante: null / undefined / NaN → `fallback`. */
export const formatFixedOr = (
  n: number | null | undefined,
  decimals = 2,
  fallback = "—"
): string => (isNumber(n) ? formatFixed(n, decimals) : fallback);

// ---------------------------------------------------------------------------
// Lectura de lo que escribe una persona
// ---------------------------------------------------------------------------

export type ParsedNumberStatus =
  /** Vacio (o solo un "-" a medio escribir). */
  | "empty"
  | "valid"
  /**
   * Un unico punto con tres cifras detras ("1.234"): en castellano se lee como
   * mil doscientos treinta y cuatro, pero aqui se ha tomado como decimal
   * (1,234). Es valido, pero merece un aviso.
   */
  | "ambiguous"
  | "invalid";

export interface ParsedNumber {
  status: ParsedNumberStatus;
  /** Numero en notacion canonica ("-1234.5"), sin separadores de miles. */
  canonical: string;
  value: number | null;
}

const EMPTY: ParsedNumber = { status: "empty", canonical: "", value: null };
const INVALID: ParsedNumber = { status: "invalid", canonical: "", value: null };

const valid = (
  sign: string,
  int: string,
  frac: string,
  status: "valid" | "ambiguous" = "valid"
): ParsedNumber => {
  const intPart = int.replace(/^0+(?=\d)/, "") || "0";
  const canonical = `${sign}${intPart}${frac ? `.${frac}` : ""}`;
  return { status, canonical, value: Number(canonical) };
};

const grouped = (int: string, sep: "." | ",") =>
  new RegExp(`^\\d{1,3}(\\${sep}\\d{3})+$`).test(int);

/**
 * Lee un numero escrito a mano. Acepta coma y punto como separador decimal:
 *
 *   "12,5"        → 12.5
 *   "12.5"        → 12.5
 *   "1.234,56"    → 1234.56   (punto de miles + coma decimal)
 *   "1,234.56"    → 1234.56   (formato ingles pegado desde fuera)
 *   "1.234.567"   → 1234567   (puntos de miles, sin decimales)
 *   "1.234"       → 1.234     (ambiguous: ¿mil doscientos o uno con 234?)
 *   "1,2,3", "12a", "1e5", "1.23.4" → invalid
 *
 * Nunca adivina en silencio con lo que no entiende: lo devuelve como invalid.
 */
export const parseLocaleNumber = (raw: string): ParsedNumber => {
  const text = raw.trim().replace(/\s/g, "");
  if (text === "" || text === "-") return EMPTY;

  const sign = text.startsWith("-") ? "-" : "";
  const body = sign ? text.slice(1) : text;
  if (!/^[\d.,]+$/.test(body) || !/\d/.test(body)) return INVALID;

  const commas = (body.match(/,/g) ?? []).length;
  const dots = (body.match(/\./g) ?? []).length;

  if (commas === 0 && dots === 0) return valid(sign, body, "");

  // Los dos separadores: el ultimo es el decimal, el otro agrupa miles.
  if (commas > 0 && dots > 0) {
    const decimal = body.lastIndexOf(",") > body.lastIndexOf(".") ? "," : ".";
    const thousands = decimal === "," ? "." : ",";
    const parts = body.split(decimal);
    if (parts.length !== 2) return INVALID;
    const [int, frac] = parts;
    if (!grouped(int, thousands as "." | ",") || /\D/.test(frac)) {
      return INVALID;
    }
    return valid(sign, int.split(thousands).join(""), frac);
  }

  const sep = (commas > 0 ? "," : ".") as "," | ".";
  const count = commas > 0 ? commas : dots;

  if (count === 1) {
    const [int, frac] = body.split(sep);
    // "," o "." al principio o al final se toleran mientras se escribe (",5", "5,").
    const looksLikeThousands =
      sep === "." && /^[1-9]\d{0,2}$/.test(int) && /^\d{3}$/.test(frac);
    return valid(sign, int, frac, looksLikeThousands ? "ambiguous" : "valid");
  }

  // Varios separadores iguales: solo valen como puntos de miles ("1.234.567").
  if (sep === "." && grouped(body, ".")) {
    return valid(sign, body.split(".").join(""), "");
  }
  return INVALID;
};

/**
 * Numero canonico (o numero JS) → texto para un input: coma decimal y sin
 * separador de miles, para que lo que se ve al editar se lea sin dudas
 * ("1234,5", nunca "1.234").
 */
export const toInputText = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") return "";
  const text = String(value).trim();
  if (/^-?\d+(\.\d+)?$/.test(text)) return text.replace(".", ",");
  const n = Number(text);
  if (!Number.isFinite(n)) return text;
  // Notacion cientifica (1e-7) → decimal plano.
  return n
    .toLocaleString(NUMBER_LOCALE, {
      useGrouping: false,
      maximumFractionDigits: 20,
    })
    .replace(/^-0$/, "0");
};
